import { APP_NAME } from 'src/constants';
import { createAndSaveEvent } from '../EventLoggingService/EventLoggingService';
import { getAllModules } from 'src/services/DBService/DbService';
import { getModuleAvailableDate, moduleIsVisibleForUser } from 'src/services/DecisionTreeService/DecisionTreeService';
import { ModuleDataDB } from '@app/common';
import { isMobileDevice } from 'src/services/CordovaService/NetworkConnectionService';

/**
 * Parsed interval from ISO 8601 duration format
 */
interface ParsedInterval {
    months: number;
    days: number;
    hours: number;
    minutes: number;
}

/**
 * Module to schedule notification data
 */
interface ModuleToSchedule {
    moduleId: string;
    moduleName: string;
    scheduleDate: Date;
    interval?: string;
    parsedInterval?: ParsedInterval;
    customTitle?: string;
    customDescription?: string;
    isRecurring: boolean;
    notificationId: number;
}

const LAST_START_DATE_USED_KEY = 'lastStartDateUsed';
const MODULE_NOTIFICATIONS_INITIALIZED_KEY = 'moduleNotificationsInitialized';
const ONE_TIME_MODULE_NOTIFICATION_SENT_PREFIX = 'oneTimeModuleNotificationSent:';

export const normalizeUserStartDate = (userStartDate: string | undefined): string => {
    return userStartDate ?? '';
};

export const stableNotificationIdForModule = (moduleId: string): number => {
    // Deterministic, reasonably low-collision 32-bit hash.
    // Ensures consistent IDs across sessions/platforms.
    let hash = 5381;
    for (let i = 0; i < moduleId.length; i++) {
        hash = ((hash << 5) + hash) ^ moduleId.charCodeAt(i);
    }

    // Convert to positive 31-bit int and avoid 0.
    const id = (hash >>> 0) & 0x7fffffff;
    return id === 0 ? 1 : id;
};

const getOneTimeSentKey = (moduleId: string) => `${ONE_TIME_MODULE_NOTIFICATION_SENT_PREFIX}${moduleId}`;

const safeParseNotificationData = (notification: any): any | undefined => {
    const data = notification?.data;
    if (!data) {
        return undefined;
    }
    if (typeof data === 'string') {
        try {
            return JSON.parse(data);
        } catch {
            return undefined;
        }
    }
    return data;
};

const getScheduledNotificationsAsync = (notificationPlugin: any): Promise<any[]> => {
    return new Promise((resolve) => {
        try {
            notificationPlugin.getScheduled((scheduledNotifications: any[]) => {
                resolve(Array.isArray(scheduledNotifications) ? scheduledNotifications : []);
            });
        } catch (e) {
            console.warn('[notifications] Failed to query scheduled notifications:', e);
            resolve([]);
        }
    });
};

/**
 * Parse ISO 8601 duration format (e.g., P1M, P7D, PT1H, PT30M)
 * Returns an object with numeric values for each time unit
 */
export const parseISO8601Duration = (interval: string): ParsedInterval | null => {
    if (!interval || typeof interval !== 'string' || !interval.startsWith('P')) {
        return null;
    }

    // Validate format using regex
    // ISO 8601 duration: P[n]Y[n]M[n]DT[n]H[n]M
    // We ignore years (Y) but allow them in the pattern
    const iso8601Pattern = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/;
    const match = interval.match(iso8601Pattern);
    
    if (!match) {
        return null;
    }

    const months = match[2] ? parseInt(match[2], 10) : 0;
    const days = match[3] ? parseInt(match[3], 10) : 0;
    const hours = match[4] ? parseInt(match[4], 10) : 0;
    const minutes = match[5] ? parseInt(match[5], 10) : 0;
    
    // Return null if no valid components were found
    if (months === 0 && days === 0 && hours === 0 && minutes === 0) {
        return null;
    }
    
    return { months, days, hours, minutes };
};

/**
 * Add parsed interval to a date
 */
export const addIntervalToDate = (baseDate: Date, interval: ParsedInterval): Date => {
    const newDate = new Date(baseDate);
    
    if (interval.months > 0) {
        newDate.setMonth(newDate.getMonth() + interval.months);
    }
    if (interval.days > 0) {
        newDate.setDate(newDate.getDate() + interval.days);
    }
    if (interval.hours > 0) {
        newDate.setHours(newDate.getHours() + interval.hours);
    }
    if (interval.minutes > 0) {
        newDate.setMinutes(newDate.getMinutes() + interval.minutes);
    }
    
    return newDate;
};

/**
 * Build a notification object for a module (pure function)
 * Returns the notification configuration object without side effects
 */
export const buildNextModuleNotificationObject = (
    notificationId: number,
    moduleId: string,
    moduleName: string,
    nextFireDate: Date,
    interval: string,
    customTitle?: string,
    customDescription?: string
) => {
    return {
        id: notificationId,
        title: customTitle || `${APP_NAME}: ${moduleName}`,
        text: customDescription || `Tap to open ${moduleName}`,
        iOSForeground: true,
        badgeNumber: 1,
        androidAutoCancel: true,
        data: {
            targetPage: moduleId,
            interval: interval, // Store interval for iOS self-rescheduling
            moduleName: moduleName,
            customTitle: customTitle,
            customDescription: customDescription
        },
        trigger: {
            at: nextFireDate
        }
    };
};

/**
 * Schedule the next occurrence of a module notification (iOS only)
 * iOS doesn't support trigger.every with arbitrary intervals on UNCalendarNotificationTrigger
 * so we manually reschedule on each trigger
 */
const scheduleNextModuleNotification = (
    notificationPlugin: any,
    notificationId: number,
    moduleId: string,
    moduleName: string,
    nextFireDate: Date,
    interval: string,
    customTitle?: string,
    customDescription?: string
) => {
    console.log(`Scheduling next notification for module ${moduleName} at ${nextFireDate.toString()}`);
    
    const notificationObject = buildNextModuleNotificationObject(
        notificationId,
        moduleId,
        moduleName,
        nextFireDate,
        interval,
        customTitle,
        customDescription
    );
    
    notificationPlugin.schedule([notificationObject]);
};

/**
 * Build a reminder notification object (pure function)
 * Returns the notification configuration object without side effects
 */
export const buildReminderNotificationObject = (studyName: string | undefined) => {
    const appName = studyName || APP_NAME;
    return {
        id: 'reminder',
        title: appName,
        text: `Das ist eine Erinnerung an deine ${appName}-Befragungen. Du hast die App schon länger nicht mehr geöffnet. Klicke hier, um die aktuelle Befragung auszufüllen.`,
        trigger: { in: 1, unit: 'day' },
        iOSForeground: true,
        androidAutoCancel: true,
        badgeNumber: 1
    };
};

/**
 * Schedule the daily reminder notification
 */
const scheduleReminderNotification = (notificationPlugin: any, studyName: string | undefined) => {
    const notificationObject = buildReminderNotificationObject(studyName);
    notificationPlugin.schedule(notificationObject);
};

/**
 * Cancel notifications for modules that are no longer visible
 */
const cancelInvisibleModuleNotifications = (
    notificationPlugin: any,
    scheduledNotifications: any[],
    allModules: ModuleDataDB[],
    moduleModifiers: string[],
    userStartDate: string | undefined,
    userProperties: string[]
) => {
    scheduledNotifications.forEach((notification: any) => {
        // Skip the reminder notification
        if (notification.id === 'reminder') {
            return;
        }

        const notificationData = safeParseNotificationData(notification);
        const targetModuleId = notificationData?.targetPage;
        if (!targetModuleId) {
            return;
        }

        const module = allModules.find(m => m._id === targetModuleId);
        if (module) {
            const isModuleVisible = moduleIsVisibleForUser(module, moduleModifiers, allModules, userStartDate, userProperties);
            const visibleStartsOn = getModuleAvailableDate(module, allModules, moduleModifiers, userStartDate, userProperties);
            const now = new Date();
            const shouldBeCancelled = visibleStartsOn ? !(visibleStartsOn >= now || isModuleVisible) : !isModuleVisible;

            if (shouldBeCancelled) {
                console.log(`Canceling notification for module ${module.name} (no longer visible)`);
                notificationPlugin.cancel(notification.id);
                localStorage.removeItem(getOneTimeSentKey(targetModuleId));
            }
        } else {
            // Module no longer exists, cancel its notification
            console.log(`Canceling notification for non-existent module (id: ${targetModuleId})`);
            notificationPlugin.cancel(notification.id);
            localStorage.removeItem(getOneTimeSentKey(targetModuleId));
        }
    });
};

/**
 * Get modules that should have notifications scheduled (pure function)
 * Returns an array of modules with their scheduling information without side effects
 */
export const getModulesToSchedule = (
    allModules: ModuleDataDB[],
    moduleModifiers: string[],
    userStartDate: string | undefined,
    userProperties: string[]
): ModuleToSchedule[] => {
    const modulesWithNotification = allModules.filter(module => module?.notifications?.enabled === true);
    const modulesToSchedule: ModuleToSchedule[] = [];
    
    modulesWithNotification.forEach((module: ModuleDataDB) => {
        const visibleStartsOn = getModuleAvailableDate(module, allModules, moduleModifiers, userStartDate, userProperties);
        const isModuleVisible = moduleIsVisibleForUser(module, moduleModifiers, allModules, userStartDate, userProperties);

        if (visibleStartsOn) {
            const now = new Date();
            if (visibleStartsOn >= now || isModuleVisible) {
                const scheduleDate = visibleStartsOn >= now ? visibleStartsOn : now;
                
                const isRecurring = module.notifications?.recurringReminders?.enabled === true;
                const interval = module.notifications?.recurringReminders?.interval;
                const parsedInterval = interval ? parseISO8601Duration(interval) : undefined;
                
                // Only include if interval is valid for recurring, or if not recurring
                if (!isRecurring || (isRecurring && parsedInterval)) {
                    modulesToSchedule.push({
                        moduleId: module._id,
                        moduleName: module.name,
                        scheduleDate,
                        interval,
                        parsedInterval: parsedInterval || undefined,
                        customTitle: module.notifications?.customTitle,
                        customDescription: module.notifications?.customDescription,
                        isRecurring,
                        notificationId: stableNotificationIdForModule(module._id)
                    });
                }
            }
        }
    });
    
    return modulesToSchedule;
};

/**
 * Build iOS module notification object (pure function)
 * For iOS recurring reminders using self-rescheduling strategy
 */
export const buildIOSModuleNotification = (moduleToSchedule: ModuleToSchedule) => {
    return buildNextModuleNotificationObject(
        moduleToSchedule.notificationId,
        moduleToSchedule.moduleId,
        moduleToSchedule.moduleName,
        moduleToSchedule.scheduleDate,
        moduleToSchedule.interval!,
        moduleToSchedule.customTitle,
        moduleToSchedule.customDescription
    );
};

/**
 * Build Android module notification object (pure function)
 * For Android recurring reminders using trigger.every
 */
export const buildAndroidModuleNotification = (moduleToSchedule: ModuleToSchedule) => {
    const everyInterval = {
        month: moduleToSchedule.parsedInterval!.months,
        day: moduleToSchedule.parsedInterval!.days,
        hour: moduleToSchedule.parsedInterval!.hours,
        minute: moduleToSchedule.parsedInterval!.minutes
    };
    
    return {
        id: moduleToSchedule.notificationId,
        title: moduleToSchedule.customTitle || `${APP_NAME}: ${moduleToSchedule.moduleName}`,
        text: moduleToSchedule.customDescription || `Tap to open ${moduleToSchedule.moduleName}`,
        iOSForeground: true,
        badgeNumber: 1,
        androidAutoCancel: true,
        data: {
            targetPage: moduleToSchedule.moduleId,
        },
        trigger: {
            at: moduleToSchedule.scheduleDate,
            every: everyInterval
        }
    };
};

/**
 * Build one-time module notification object (pure function)
 * For non-recurring module notifications
 */
export const buildOneTimeModuleNotification = (moduleToSchedule: ModuleToSchedule) => {
    return {
        id: moduleToSchedule.notificationId,
        title: moduleToSchedule.customTitle || `${APP_NAME}: ${moduleToSchedule.moduleName}`,
        text: moduleToSchedule.customDescription || `Tap to open ${moduleToSchedule.moduleName}`,
        iOSForeground: true,
        badgeNumber: 1,
        androidAutoCancel: true,
        data: {
            targetPage: moduleToSchedule.moduleId,
        },
        trigger: {
            at: moduleToSchedule.scheduleDate
        }
    };
};

/**
 * Schedule notifications for all visible modules
 */
const scheduleVisibleModuleNotifications = (
    notificationPlugin: any,
    scheduledNotifications: any[],
    allModules: ModuleDataDB[],
    moduleModifiers: string[],
    userStartDate: string | undefined,
    userProperties: string[],
    isIOS: boolean
) => {
    const modulesToSchedule = getModulesToSchedule(allModules, moduleModifiers, userStartDate, userProperties);

    const normalizedStartDate = normalizeUserStartDate(userStartDate);
    const existingScheduledIds = new Set<number | string>();
    const existingScheduledModuleIds = new Set<string>();
    scheduledNotifications.forEach((n: any) => {
        if (n?.id !== undefined) {
            existingScheduledIds.add(n.id);
        }
        const data = safeParseNotificationData(n);
        if (data?.targetPage) {
            existingScheduledModuleIds.add(data.targetPage);
        }
    });
    
    modulesToSchedule.forEach((moduleToSchedule) => {
        // De-dup: skip if a notification is already scheduled for this module.
        if (existingScheduledIds.has(moduleToSchedule.notificationId) || existingScheduledModuleIds.has(moduleToSchedule.moduleId)) {
            return;
        }

        // One-time notifications should only ever fire once per module (and per startDate),
        // even if scheduling runs repeatedly due to navigation/userdata changes.
        if (!moduleToSchedule.isRecurring) {
            const sentKey = getOneTimeSentKey(moduleToSchedule.moduleId);
            if (localStorage.getItem(sentKey) === normalizedStartDate) {
                return;
            }
        }

        console.log(`Scheduling notification for module ${moduleToSchedule.moduleName} at ${moduleToSchedule.scheduleDate.toString()}`);

        let notificationObject;
        
        if (moduleToSchedule.isRecurring && moduleToSchedule.parsedInterval) {
            console.log(`Setting up recurring reminders for module ${moduleToSchedule.moduleName} with interval ${moduleToSchedule.interval}`);
            console.log(`Parsed recurring interval for module ${moduleToSchedule.moduleName}: `, moduleToSchedule.parsedInterval);
            
            if (isIOS) {
                notificationObject = buildIOSModuleNotification(moduleToSchedule);
            } else {
                notificationObject = buildAndroidModuleNotification(moduleToSchedule);
            }
        } else {
            notificationObject = buildOneTimeModuleNotification(moduleToSchedule);
        }
        
        notificationPlugin.schedule([notificationObject]);

        if (!moduleToSchedule.isRecurring) {
            localStorage.setItem(getOneTimeSentKey(moduleToSchedule.moduleId), normalizedStartDate);
        }
    });
};

export const scheduleNotifications = (moduleModifiers: string[], userStartDate: string | undefined, userProperties: string[], studyName: string | undefined) => {
    if (isMobileDevice()) {
        const plugins: any = window.cordova.plugins;
        const isIOS = (window as any).cordova?.platformId === 'ios';
        
        try {
            const notificationPlugin = plugins.notification.local;
            notificationPlugin.hasPermission((granted: boolean) => {
                if (granted === true) {
                    // Schedule app-level reminder notification (separate from module notifications)
                    scheduleReminderNotification(notificationPlugin, studyName);

                    // notifications for modules
                    console.log('Scheduling module notifications, userStartDate: ', userStartDate);
                    const normalizedStartDate = normalizeUserStartDate(userStartDate);
                    const moduleNotificationsInitialized = localStorage.getItem(MODULE_NOTIFICATIONS_INITIALIZED_KEY);
                    const startDateChanged = normalizedStartDate !== (localStorage.getItem(LAST_START_DATE_USED_KEY) ?? '');
                    if (startDateChanged || !moduleNotificationsInitialized) {
                        getAllModules().then(allModules => {
                            try {
                                getScheduledNotificationsAsync(notificationPlugin).then((scheduledNotifications) => {
                                    console.log(`[notifications] Scheduled notifications: ${scheduledNotifications.length}`);

                                    // Cancel notifications for modules that are no longer visible
                                    cancelInvisibleModuleNotifications(
                                        notificationPlugin,
                                        scheduledNotifications,
                                        allModules,
                                        moduleModifiers,
                                        userStartDate,
                                        userProperties
                                    );

                                    // Schedule notifications for visible modules
                                    scheduleVisibleModuleNotifications(
                                        notificationPlugin,
                                        scheduledNotifications,
                                        allModules,
                                        moduleModifiers,
                                        userStartDate,
                                        userProperties,
                                        isIOS
                                    );
                                });
                            } catch (error) {
                                console.error('Something went wrong with scheduling module notifications: ', error);
                            }
                            localStorage.setItem(MODULE_NOTIFICATIONS_INITIALIZED_KEY, 'true');
                            localStorage.setItem(LAST_START_DATE_USED_KEY, normalizedStartDate);
                        }).catch(err => {
                            console.error('Something went wrong with getting all modules: ', err);
                        });
                    } else {
                        notificationPlugin.iOSClearBadge();
                    }
                } else {
                    console.warn('permission to display notifications denied');
                }
            });
        } catch (e) {
            console.warn(e);
        }
    } else {
        console.warn('Platform is "browser". Notifications disabled.');
    }
};

// function getTimeToday(hour: number, minute: number) {
//     var now = new Date();
//     now.setHours(hour, minute, 0, 0);  // Set the time to the specific hour and minute
//     return now;
// }

export const registerNotificationClickEvents = (userId: string | undefined) => {
    if (isMobileDevice()) {
        const notificationPlugin = (window.cordova.plugins as any).notification.local;

        // Register the on-click listener
        notificationPlugin.on('click', (notification: any) => {
            const savedNotification = {
                title: notification.title,
                text: notification.text
            };
            createAndSaveEvent(userId, 'notification-clicked', JSON.stringify(savedNotification));

            try {
                const targetPage = notification.data?.targetPage;
                console.log('[notification clicked] targetPage: ', targetPage);
                if (targetPage) {
                    localStorage.setItem('pendingRedirect', targetPage);
                }

                notificationPlugin.iOSClearBadge();
            } catch (e) {
                console.error('Error while saving target path to localstorage', e);
            }
        });
        
        // Register the on-trigger listener for iOS self-rescheduling
        // iOS doesn't support arbitrary repeating intervals with UNCalendarNotificationTrigger
        // so we manually reschedule recurring notifications when they fire
        notificationPlugin.on('trigger', (notification: any) => {
            // Skip the reminder notification
            if (notification.id === 'reminder') {
                return;
            }
            
            // Parse data if it's a string (iOS serializes it as JSON string)
            let notificationData = notification.data;
            if (typeof notificationData === 'string') {
                try {
                    notificationData = JSON.parse(notificationData);
                } catch (e) {
                    console.error('[notification triggered] Failed to parse notification data:', e);
                    return;
                }
            }
            
            // Check if this notification has a recurring interval (iOS only)
            const interval = notificationData?.interval;
            if (interval) {
                console.log('[notification triggered] Rescheduling notification:', notification.id);
                
                const parsedInterval = parseISO8601Duration(interval);
                if (parsedInterval) {
                    const now = new Date();
                    const nextFireDate = addIntervalToDate(now, parsedInterval);
                    
                    // Reschedule the next occurrence with the same numeric ID
                    scheduleNextModuleNotification(
                        notificationPlugin,
                        notification.id,
                        notificationData.targetPage,
                        notificationData.moduleName,
                        nextFireDate,
                        interval,
                        notificationData.customTitle,
                        notificationData.customDescription
                    );
                } else {
                    console.error('[notification triggered] Failed to parse interval:', interval);
                }
            }
        });
        
        // Fire the queued events manually (auto firing was disabled in index.html)
        try {
            notificationPlugin.fireQueuedEvents();
        } catch (e) {
            console.error('Error while firing queued events', e);
        }
    }
};

export const cancelNotificationForModule = (moduleId: string) => {
    if (isMobileDevice()) {
        const plugins: any = window.cordova.plugins;
        
        try {
            const notificationPlugin = plugins.notification.local;
            console.log(`[cancelNotificationForModule] Attempting to cancel notifications for module: ${moduleId}`);
            
            notificationPlugin.getScheduled((scheduledNotifications: any[]) => {
                console.log(`[cancelNotificationForModule] Total scheduled notifications: ${scheduledNotifications.length}`);
                
                let foundCount = 0;
                scheduledNotifications.forEach((notification: any) => {
                    console.log(`[cancelNotificationForModule] Checking notification ID ${notification.id}`);
                    
                    // Parse data if it's a string (iOS serializes it as JSON string)
                    let notificationData = notification.data;
                    if (typeof notificationData === 'string') {
                        try {
                            notificationData = JSON.parse(notificationData);
                        } catch (e) {
                            console.warn(`[cancelNotificationForModule] Failed to parse notification data:`, e);
                            return;
                        }
                    }
                    
                    const targetModuleId = notificationData?.targetPage;
                    console.log(`[cancelNotificationForModule] targetPage: ${targetModuleId}`);
                    
                    if (targetModuleId === moduleId) {
                        foundCount++;
                        console.log(`[cancelNotificationForModule] MATCH FOUND - Canceling notification ID ${notification.id} for module ${moduleId}`);
                        notificationPlugin.cancel(notification.id);
                    }
                });
                
                console.log(`[cancelNotificationForModule] Cancelled ${foundCount} notification(s) for module ${moduleId}`);
            });
        } catch (e) {
            console.warn('Error canceling notification for module:', e);
        }
    }
};

export const clearIosBadgeNumber = () => {
    if (isMobileDevice()) {
        const plugins: any = window.cordova.plugins;
        try {
            const notificationPlugin = plugins.notification.local;
            notificationPlugin.hasPermission((granted: boolean) => {
                if (granted === true) {
                    notificationPlugin.iOSClearBadge();
                }
            });
        } catch (e) {
            console.warn(e);
        }
    }
};